/**
 * monetization/providers — provider-agnostic payment abstraction (client side).
 *
 * The client NEVER talks to the gateway directly: the server exposes
 * POST /api/billing/checkout and the provider injected there decides (Rust trait
 * `PaymentProvider`). This TS mirror describes how the UI must INTERPRET the
 * active provider: whether it offers automatic checkout or if the upgrade is a manual
 * flow (provider "none": applied by an admin).
 *
 * Adding a new provider = register its descriptor here + implement the
 * trait in Rust. No UI screen or domain logic changes.
 */

import {
  clampProviderToPlatform,
  isStoreBinaryPlatform,
  normalizePlatform,
  type PurchaseGateReason,
  type ResolvedPurchasePlatform,
  UNKNOWN_PURCHASE_PLATFORM,
} from "./purchase";

export interface CheckoutResult {
  provider: string;
  /** Hosted payment URL to redirect to (if offered by the provider). */
  checkoutUrl: string | null;
  /** true ⇒ manual upgrade (contact / applied by an admin). */
  manual: boolean;
}

/** Descriptor of a payment provider, as seen by the UI. */
export interface PaymentProviderDescriptor {
  id: string;
  /** Does the upgrade open a hosted checkout? (false ⇒ manual flow). */
  supportsCheckout: boolean;
  /** Does the server receive webhooks from this provider? */
  supportsWebhooks: boolean;
}

const NONE_PROVIDER: PaymentProviderDescriptor = {
  id: "none",
  supportsCheckout: false,
  supportsWebhooks: false,
};

const REGISTRY: Record<string, PaymentProviderDescriptor> = {
  none: NONE_PROVIDER,
  stripe: { id: "stripe", supportsCheckout: true, supportsWebhooks: true },
};

/** Registered ids (for the admin panel Select). */
export const PAYMENT_PROVIDER_IDS = Object.keys(REGISTRY);

/** Descriptor of the active provider; unknown ⇒ behaves like "none". */
export function getPaymentProvider(id: string | undefined): PaymentProviderDescriptor {
  return REGISTRY[id ?? "none"] ?? NONE_PROVIDER;
}

/**
 * Interprets the server checkout response in a provider-agnostic manner:
 * returns the action that the UI must execute.
 *
 * `blocked` ⇒ **nowhere to navigate**: the surface does not allow that
 * purchase (see `monetization/purchase.ts`).
 */
export type CheckoutAction =
  | { kind: "redirect"; url: string }
  | { kind: "manual" }
  | { kind: "blocked"; reason: PurchaseGateReason };

/**
 * **Client second door, and the one that truly closes.**
 *
 * `platform` is a MANDATORY parameter on purpose. The previous version of
 * this function lacked it and returned `redirect` with whatever URL arrived
 * from the server; `location.assign` on the plan screen did the rest.
 * Making it mandatory turns omissions into a **type error** in every
 * clone: no need to remember the guard, it will not compile without it. (And it is
 * deliberately a type and not a lint: in this fleet guards that only
 * checked text guarded nothing.)
 *
 * Rules, all fail-closed:
 *   * unclassified surface ⇒ `blocked`;
 *   * provider returned by server that is not legal on this surface
 *     (Stripe in the APK) ⇒ `blocked`;
 *   * **store binary ⇒ never navigate to a payment URL**, not even
 *     with a legal provider: Play policy also pursues
 *     *link-out*;
 *   * if no URL or manual flow ⇒ `manual`, as always.
 */
export function interpretCheckout(
  result: CheckoutResult,
  platform: ResolvedPurchasePlatform | string,
): CheckoutAction {
  const surface = normalizePlatform(platform);
  if (surface === UNKNOWN_PURCHASE_PLATFORM) {
    return { kind: "blocked", reason: "unknown-platform" };
  }
  const provider = result.provider.trim().toLowerCase();
  if (clampProviderToPlatform(surface, provider) !== provider) {
    return { kind: "blocked", reason: "provider-not-allowed" };
  }
  if (!result.manual && result.checkoutUrl) {
    if (isStoreBinaryPlatform(surface)) {
      // Belt and suspenders: even if someday a provider with a URL were legal
      // in a store binary, outbound navigation is not.
      return { kind: "blocked", reason: "provider-not-allowed" };
    }
    return { kind: "redirect", url: result.checkoutUrl };
  }
  return { kind: "manual" };
}
